'use server';

import { headers } from 'next/headers';
import { VERCEL_HEADERS } from 'shared-utils/headers/constants';
import { logServerEvent } from 'shared-utils/server-logging';

interface FormExposeContext {
  formId: string;
  formTemplate: string;
  formKind: 'inline' | 'modal' | 'multi-field-inline' | 'vendor-claim';
  brand?: string;
  placement?: string;
  page_type?: string;
  page_path?: string;
  eloqua_campaign?: string;
}

/**
 * Read the first comma-separated value of a Vercel/Cloudflare header.
 * Mirrors the parsing in `apps/bx-capterra-www/utils/getVercelHeaders.ts`,
 * which we can't import directly across the package boundary.
 */
function firstHeaderValue(value: string | null | undefined): string | null {
  if (!value) return null;
  const first = value.split(',')[0]?.trim();
  return first || null;
}

/**
 * Record a `bx_forms.expose.recorded` server log when a form actually opens
 * for a user (matches `analytics.trackFormOpen()` lifecycle: form scrolls
 * into view for inline forms; modal `isOpen` flips to true for modal forms).
 *
 * Invoked from the client via Next.js Server Actions, so the log lands on
 * Vercel's stdout Log Drain → Datadog Logs index, identical to the path
 * `bx_forms.submit.success` uses. Combined, DD computes submit:expose
 * conversion ratios per form / surface / brand / page_type.
 *
 * Context keyset mirrors the gateway's `buildLogContext` exactly so a UNION
 * across the two events sees the same dimensions. `country` is the raw
 * 2-letter code from `x-geo-country-code` (submit logs the resolved country
 * name like "United States" via app-private `getCountryName`/`COUNTRY_LIST`
 * — moving that helper to a shared package would expand scope; for now
 * accept the format mismatch on the country dimension specifically).
 * `is_mobile` matches submit's logic exactly (`deviceType === 'mobile'`).
 */
export async function recordFormExposeAction(
  ctx: FormExposeContext
): Promise<void> {
  if (!ctx.formId) return;

  const requestHeaders = await headers();
  const countryCode = firstHeaderValue(
    requestHeaders.get(VERCEL_HEADERS.COUNTRY)
  );
  const deviceType = firstHeaderValue(
    requestHeaders.get(VERCEL_HEADERS.DEVICE_TYPE)
  );

  logServerEvent({
    event: 'bx_forms.expose.recorded',
    level: 'info',
    service: 'bx-capterra',
    route: `/bx-forms/${ctx.formKind}`,
    message: 'Form opened',
    context: {
      formId: ctx.formId,
      formTemplate: ctx.formTemplate,
      brand: ctx.brand,
      placement: ctx.placement,
      page_type: ctx.page_type,
      page_path: ctx.page_path ?? null,
      country: countryCode,
      is_mobile: deviceType === 'mobile',
      eloqua_campaign: ctx.eloqua_campaign,
    },
  });
}
